<script setup>
import moment from "moment";
import { computed, inject, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import ArgonTable from "@/components/table/ArgonTable.vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import CopyValue from "@/components/CopyValue.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import UserTypeBadge from "@/components/badges/UserType.vue";
import { argonTableStore } from "@/_store/argonTableStore";
import { stopStore } from "@/_store/stopStore";
import StopDetailModal from "@/views/plans/components/stop-detail/StopDetailModal.vue";
import StopDateModal from "@/views/plans/components/stop-detail/StopDateModal.vue";
import StopRetryDeliveryModal from "@/views/plans/components/stop-detail/StopRetryDeliveryModal.vue";
import StopTimeWindowModal from "@/views/plans/components/stop-detail/StopTimeWindowModal.vue";
import StopNormalizeAddressModal from "@/views/plans/components/stop-detail/StopNormalizeAddressModal.vue";
import CreateManualStopModal from "@/views/stops/components/CreateManualStopModal.vue";
import DecaReviewModal from "@/views/plans/components/DecaReviewModal.vue";
import { decaStore } from "@/_store/decaStore";
import { errorUtils, fileUtils } from "@/utils";
import { defaultFilterDates } from "@/maestros/defaultDatesValues";
import ESTATUS_ENUM from "@/Constans/EstatusEnum";
import { userStore } from "@/_store/userStore";
import {
  formatAlertsTooltipHtml,
  formatDeliveryInfoSummary,
  formatStopTypeLabel,
  formatTimeWindow,
  hasDeliveryInfoSummary,
  normalizeAlerts,
} from "@/utils/formatters";
const router = useRouter();
const tableStore = argonTableStore();
const stopsStore = stopStore();
const decaStores = decaStore();
const userStores = userStore();
const sweetAlert = inject("$swal", null);
const snackbar = ref(null);
const showStopDetailModal = ref(false);
const showStopDateModal = ref(false);
const showStopRetryDeliveryModal = ref(false);
const showStopTimeWindowModal = ref(false);
const showStopNormalizeAddressModal = ref(false);
const showCreateManualStopModal = ref(false);
const showDecaReviewModal = ref(false);
const decaReviewStopId = ref("");
const decaReviewIsModify = ref(false);
const selectedManualStopId = ref(null);
const duplicateSourceStopId = ref(null);
const selectedStopId = ref(null);
const selectedStopIds = ref([]);
const selectedStopRow = computed(() => {
  const selectedIds = getSelectedStopIds();
  if (selectedIds.length === 0) return null;
  const rows = Array.isArray(tableItems.value?.data) ? tableItems.value.data : [];
  return rows.find((item) => item?._id === selectedIds[0]) || null;
});
const selectedPlanId = computed(() => selectedStopRow.value?.assignment_info?.plan_id || "");
const hasSelectedStop = computed(() => getSelectedStopIds().length > 0);
const canOpenPlan = computed(
  () => Boolean(selectedPlanId.value) && getSelectedStopIds().length <= 1
);
const canNormalizeAddress = computed(() => getSelectedStopIds().length === 1);
const normalizeAddressTooltip = computed(() => {
  if (canNormalizeAddress.value) return "";
  return "Debe seleccionar una única parada para modificar la dirección.";
});
const openPlanTooltip = computed(() => {
  if (getSelectedStopIds().length > 1) {
    return "Debe seleccionar una única parada para ir al plan.";
  }
  if (hasSelectedStop.value && !selectedPlanId.value) {
    return "La parada seleccionada no tiene plan asociado.";
  }
  return "";
});


const fieldFilters = ref([
  {
    field: "number",
    description: "Nº parada",
    exact: true,
  },
  {
    field: "shipper_info.name",
    description: "Cargador",
  },
  {
    field: "assignment_info.plan_name",
    description: "Nombre del plan",
  },
  {
    field: "address_normalized.address",
    description: "Dirección",
  },
]);
const customFilterDate = ref(stopsStore.customFilterDate);
const stopStatusOptions = Object.entries(ESTATUS_ENUM.stop).map(
  ([value, meta]) => ({
    label: meta?.label || value,
    value,
  })
);

const defaultFilters = ref([
  {
    field: "status",
    label: "Estado",
    multi: true,
    options: stopStatusOptions,
  },
  {
    field: "type",
    label: "Tipo",
    multi: false,
    options: [
      { label: "Recogida", value: "RECOGIDA" },
      { label: "Entrega", value: "ENTREGA" },
    ],
  },
  {
    field: "has_alerts",
    label: "Alertas",
    multi: false,
    options: [
      { label: "Con alertas", value: true },
      { label: "Sin alertas", value: false },
    ],
  },
  {
    field: "has_deca",
    label: "DeCA",
    multi: false,
    options: [
      { label: "Sí", value: true },
    ],
  },
  {
    field: "date",
    label: "Fecha",
    multi: false,
    isDate: true,
    options: [
      ...defaultFilterDates,
      {
        label: "Personalizado",
        value: customFilterDate,
        isCustomDate: true,
        storage: stopsStore,
        formatDate: "DD/MM/YYYY",
      },
    ],
  },
]);

const importOptions = ref({
  title: "Importar Paradas Manuales",
  alertMsg: "Crea paradas manuales en bloque a partir de un Excel (.xlsx) con las hojas \"Paradas\" y \"Bultos\". La columna \"parada_id\" solo sirve para relacionar cada bulto con su parada dentro del propio fichero, no se guarda. Descarga el ejemplo para ver el formato y las columnas obligatorias.",
  options: [],
  url: "/ttmm/stop/manual/excel",
  templateLinkText: "Descargar ejemplo de Excel",
});

async function downloadExcelTemplate() {
  const bytes = await stopsStore.downloadManualExcelTemplate();
  fileUtils.downloadBytesFile(bytes, "plantilla_paradas_manuales.xlsx");
}

const tableItems = computed(() => stopsStore.stops);

const columns = ref([ 
  { header: "Fecha", field: "date", sort: false, position: "start" },
  { header: "Nº parada", field: "number", sort: false, position: "start" },
  { header: "Tipo", field: "type", sort: false, position: "center" },
  { header: "Cargador", field: "shipper_info.name", sort: false, position: "start" },
  { header: "Horario", field: "time_window_1", sort: false, position: "start" },
  {
    header: "Dirección",
    field: "address_normalized.address",
    sort: false,
    position: "start",
  },
  {
    header: "Plan",
    field: "assignment_info?.plan_name",
    sort: false,
    position: "start",
  },
  {
    header: "Alertas",
    field: "alerts",
    sort: false,
    position: "center",
     
  },
  {
    header: "Info reparto",
    field: "delivery_info.summary",
    sort: false,
    position: "center",
  },
  {
    header: "DeCA",
    field: "has_deca",
    sort: false,
    position: "center",
  },
  {
    header: "Estado",
    field: "status",
    sort: false,
    position: "center",
    width: "w-5",
  },
  {
    header: "",
    field: "actions",
    sort: false,
    position: "center",
    width: "w-1",
  },
]);

function getPosition(field) {
  const column = columns.value.find((col) => col.field === field);
  return column?.position || "start";
}

function getStopTypeValue(stopItem) {
  return String(stopItem?.type || stopItem?.stop_type || "").toUpperCase();
}

function getInputAddress(item) {
  return item?.address_normalized?.address ?? "";
}

function getWarningItems(item) {
  return normalizeAlerts(item?.alerts);
}

function getStatusTooltip(item) {
  const status = String(item?.status || "").toUpperCase();
  const description = item?.delivery_info?.status_description ?? "";

  if (status === "INCIDENCIA" && description) {
    return description;
  }

  return "";
}

function reloadPage() {
  userStores.filtersPage.stops = {
    filters: tableStore.filters,
    filtersFavorite: tableStore.filtersFavorite,
  };
  stopsStore.getStops(tableStore.pagination, tableStore.query, tableStore.sort);
}

async function exportStops() {
  const bytes = await stopsStore.exportar(
    tableStore.pagination,
    tableStore.query,
    tableStore.sort
  );

  fileUtils.downloadBytesFile(
    bytes,
    fileUtils.generateDownloadFileName("xlsx")
  );
}

function onSortChanged() {
  tableStore.clearPagination();
  reloadPage();
}

function closeSnackbar() {
  snackbar.value = null;
}

function openCreateManualStopModal() {
  selectedManualStopId.value = null;
  duplicateSourceStopId.value = null;
  showCreateManualStopModal.value = true;
}

function openModifyManualStopModal(stopId) {
  if (!stopId) return;
  selectedManualStopId.value = stopId;
  duplicateSourceStopId.value = null;
  showCreateManualStopModal.value = true;
}

function openCreateDeliveryFromPickupModal(stopId) {
  if (!stopId) return;
  selectedManualStopId.value = null;
  duplicateSourceStopId.value = stopId;
  showCreateManualStopModal.value = true;
}

function closeCreateManualStopModal() {
  showCreateManualStopModal.value = false;
  selectedManualStopId.value = null;
  duplicateSourceStopId.value = null;
}

function onCreateManualStopSuccess() {
  const wasEdit = !!selectedManualStopId.value;
  closeCreateManualStopModal();
  reloadPage();
  snackbar.value = {
    title: "OK",
    message: wasEdit ? "Parada modificada correctamente" : "Parada creada correctamente",
    type: "info",
    color: "white",
  };
}

function modifyManualForStop(stopItem) {
  openModifyManualStopModal(stopItem?._id);
}

function createDeliveryFromPickupForStop(stopItem) {
  openCreateDeliveryFromPickupModal(stopItem?._id);
}

function openStopDetailModal(stopItem) {
  const stopId = stopItem?._id ?? "";
  if (!stopId) return;
  selectedStopId.value = stopId;
  selectedStopIds.value = [stopId];
  showStopDetailModal.value = true;
  showStopDateModal.value = false;
  showStopTimeWindowModal.value = false;
  showStopNormalizeAddressModal.value = false;
}

function closeStopDetailModal() {
  showStopDetailModal.value = false;
  if (
    !showStopDateModal.value &&
    !showStopTimeWindowModal.value &&
    !showStopNormalizeAddressModal.value
  ) {
    selectedStopId.value = null;
    selectedStopIds.value = [];
  }
}

function closeStopDateModal() {
  showStopDateModal.value = false;
  if (
    !showStopDetailModal.value &&
    !showStopRetryDeliveryModal.value &&
    !showStopTimeWindowModal.value &&
    !showStopNormalizeAddressModal.value
  ) {
    selectedStopId.value = null;
    selectedStopIds.value = [];
  }
}

function closeStopRetryDeliveryModal() {
  showStopRetryDeliveryModal.value = false;
  if (
    !showStopDetailModal.value &&
    !showStopDateModal.value &&
    !showStopTimeWindowModal.value &&
    !showStopNormalizeAddressModal.value
  ) {
    selectedStopId.value = null;
    selectedStopIds.value = [];
  }
}

function closeStopTimeWindowModal() {
  showStopTimeWindowModal.value = false;
  if (
    !showStopDetailModal.value &&
    !showStopDateModal.value &&
    !showStopRetryDeliveryModal.value &&
    !showStopNormalizeAddressModal.value
  ) {
    selectedStopId.value = null;
    selectedStopIds.value = [];
  }
}

function closeStopNormalizeAddressModal() {
  showStopNormalizeAddressModal.value = false;
  if (
    !showStopDetailModal.value &&
    !showStopDateModal.value &&
    !showStopRetryDeliveryModal.value &&
    !showStopTimeWindowModal.value
  ) {
    selectedStopId.value = null;
    selectedStopIds.value = [];
  }
}

function getSelectedStopIds() {
  return Array.isArray(tableStore.checkedList)
    ? tableStore.checkedList.map((id) => id ?? "").filter(Boolean)
    : [];
}

function showSelectionError(message) {
  snackbar.value = {
    title: "Error",
    message,
    type: "danger",
    color: "white",
  };
}

function resolveSingleSelectedStopId() {
  const selectedIds = getSelectedStopIds();
  if (selectedIds.length === 0) {
    showSelectionError("Debe seleccionar una única parada.");
    return "";
  }

  if (selectedIds.length > 1) {
    showSelectionError("Solo puede seleccionar una parada.");
    return "";
  }

  return selectedIds[0];
}

function changeDate() {
  const stopIds = getSelectedStopIds();
  if (stopIds.length === 0) return;
  selectedStopIds.value = stopIds;
  selectedStopId.value = stopIds[0];
  showStopDateModal.value = true;
  showStopRetryDeliveryModal.value = false;
  showStopTimeWindowModal.value = false;
  showStopNormalizeAddressModal.value = false;
  showStopDetailModal.value = false;
}

function retryDelivery() {
  const stopIds = getSelectedStopIds();
  if (stopIds.length === 0) return;
  selectedStopIds.value = stopIds;
  selectedStopId.value = stopIds[0];
  showStopRetryDeliveryModal.value = true;
  showStopDateModal.value = false;
  showStopTimeWindowModal.value = false;
  showStopNormalizeAddressModal.value = false;
  showStopDetailModal.value = false;
}

 

function normalizeAddress() {
  if (!canNormalizeAddress.value) {
    if (getSelectedStopIds().length === 0) {
      showSelectionError("Debe seleccionar una única parada.");
    } else {
      showSelectionError("Solo puede seleccionar una parada.");
    }
    return;
  }

  const stopId = resolveSingleSelectedStopId();
  if (!stopId) return;
  selectedStopId.value = stopId;
  selectedStopIds.value = [stopId];
  showStopNormalizeAddressModal.value = true;
  showStopTimeWindowModal.value = false;
  showStopDateModal.value = false;
  showStopDetailModal.value = false;
}

function openPlan() {
  const selectedIds = getSelectedStopIds();
  if (selectedIds.length > 1) {
    showSelectionError("Solo puede seleccionar una parada.");
    return;
  }

  if (!selectedPlanId.value) {
    showSelectionError("La parada seleccionada no tiene plan asociado.");
    return;
  }

  router.push(`/plan/${selectedPlanId.value}`);
}

function onStopActionSuccess() {
  showStopDateModal.value = false;
  showStopRetryDeliveryModal.value = false;
  showStopTimeWindowModal.value = false;
  showStopNormalizeAddressModal.value = false;
  selectedStopId.value = null;
  selectedStopIds.value = [];
  closeSnackbar();
  tableStore.clearSelection();
  reloadPage();
  snackbar.value = {
    title: "OK",
    message: "Parada actualizada correctamente",
    type: "info",
    color: "white",
  };
}

async function confirmCancelStops(title) {
  const confirmOptions = {
    title,
    text: "¡No podrá revertir esta acción!",
    showCancelButton: true,
    confirmButtonText: "Aceptar",
    cancelButtonText: "Cancelar",
    reverseButtons: true,
    customClass: {
      confirmButton: "btn btn-xs ms-1 bg-gradient-danger",
      cancelButton: "btn btn-xs btn-outline-primary me-1",
    },
    buttonsStyling: false,
  };

  const result =
    typeof sweetAlert === "function"
      ? await sweetAlert(confirmOptions)
      : { isConfirmed: window.confirm(title) };
  return Boolean(result?.isConfirmed);
}

async function deleteStop() {
  const ids = getSelectedStopIds();
  if (ids.length === 0) {
    showSelectionError("Debe seleccionar al menos una parada.");
    return;
  }
  const confirmed = await confirmCancelStops(
    "¿Está seguro de querer excluir las paradas?"
  );
  if (!confirmed) return;
  stopsStore.deleteStop(ids);
}

async function deleteStopForStop(stopItem) {
  const stopId = stopItem?._id ?? "";
  if (!stopId) return;
  const confirmed = await confirmCancelStops(
    "¿Está seguro de querer excluir la parada?"
  );
  if (!confirmed) return;
  stopsStore.deleteStop([stopId]);
}

function getRowActionsMenuId(stopItem) {
  return `stop-row-actions-${String(stopItem?._id || "unknown").replace(
    /[^a-zA-Z0-9_-]/g,
    "_"
  )}`;
}

function getRowActions(stopItem) {
  const hasPlan = Boolean(stopItem?.assignment_info?.plan_id);
  const status = String(stopItem?.status || "").toUpperCase();
  const isNoAsignada = status === "NO_ASIGNADA";
  const isManual = stopItem?.manual_type === "MANUAL";
  const isLoadUnload = stopItem?.manual_type === "CARGA" || stopItem?.manual_type === "DESCARGA";
  const canRetry = (status === "INCIDENCIA" || status === "FINALIZADO_PARCIAL") && !isLoadUnload;
  const isEntrega = getStopTypeValue(stopItem) === "ENTREGA";
  const decaGenerated = Boolean(stopItem?.has_deca);
  const canManageDeca = !["FINALIZADO", "FINALIZADO_PARCIAL", "INCIDENCIA"].includes(status);

  return [
    [
      {
        label: "Ir a plan",
        event: "openPlan",
        disabled: !hasPlan,
        tooltip: hasPlan ? "" : "La parada no tiene plan asociado.",
      }
    ],
    isManual
      ? [
          {
            label: "Modificar",
            event: "modifyManual",
            disabled: !isNoAsignada,
            tooltip: isNoAsignada ? "" : "Solo disponible para paradas no asignadas.",
          },
          ...(isEntrega
            ? []
            : [
                {
                  label: "Crear entrega",
                  event: "createDeliveryFromPickup",
                  disabled: false,
                  tooltip: "",
                },
              ]),
        ]
      : [
          {
            label: "Normalizar Dirección",
            event: "normalizeAddress",
            disabled: !isNoAsignada,
            tooltip: isNoAsignada ? "" : "Solo disponible para paradas no asignadas.",
          },
          {
            label: "Modificar Fecha",
            event: "changeDate",
            disabled: !isNoAsignada,
            tooltip: isNoAsignada ? "" : "Solo disponible para paradas no asignadas.",
          },
        ],
    [
      {
        label: "Nuevo Intento de Reparto",
        event: "retryDelivery",
        disabled: !canRetry,
        tooltip: canRetry
          ? ""
          : isLoadUnload
            ? "No disponible para paradas de carga/descarga."
            : "Solo disponible para paradas con incidencia o parcial.",
      },
    ],
    isEntrega
      ? [
          {
            label: decaGenerated ? "Modificar DeCA" : "Generar DeCA",
            event: "generateDeca",
            disabled: isNoAsignada || !canManageDeca,
            tooltip: isNoAsignada
              ? "La parada no está asignada."
              : !canManageDeca
                ? "La parada ya está finalizada, no se puede generar/modificar su DeCA."
                : "",
          },
          {
            label: "Imprimir DeCA",
            event: "printDeca",
            disabled: !decaGenerated,
            tooltip: decaGenerated ? "" : "Esta parada no tiene DeCA generado.",
          },
        ]
      : [],
    [
      {
        label: "Excluir",
        event: "deleteStop",
        type: "danger",
        disabled: !isNoAsignada,
        tooltip: isNoAsignada ? "" : "Solo se puede excluir cuando esta en estado No asignada.",
      },
    ],
  ];
}

function selectStopRow(stopItem) {
  const stopId = stopItem?._id ?? "";
  if (!stopId) return "";

  selectedStopId.value = stopId;
  selectedStopIds.value = [stopId];
  return stopId;
}

function openPlanForStop(stopItem) {
  const planId = stopItem?.assignment_info?.plan_id;
  if (!planId) {
    showSelectionError("La parada seleccionada no tiene plan asociado.");
    return;
  }

  router.push(`/plan/${planId}`);
}

function changeDateForStop(stopItem) {
  if (!selectStopRow(stopItem)) return;
  showStopDateModal.value = true;
  showStopRetryDeliveryModal.value = false;
  showStopTimeWindowModal.value = false;
  showStopNormalizeAddressModal.value = false;
  showStopDetailModal.value = false;
}

function retryDeliveryForStop(stopItem) {
  if (!selectStopRow(stopItem)) return;
  showStopRetryDeliveryModal.value = true;
  showStopDateModal.value = false;
  showStopTimeWindowModal.value = false;
  showStopNormalizeAddressModal.value = false;
  showStopDetailModal.value = false;
}

function normalizeAddressForStop(stopItem) {
  if (!selectStopRow(stopItem)) return;
  showStopNormalizeAddressModal.value = true;
  showStopTimeWindowModal.value = false;
  showStopDateModal.value = false;
  showStopRetryDeliveryModal.value = false;
  showStopDetailModal.value = false;
}

function openDecaReviewForStop(stopItem) {
  const stopId = String(stopItem?._id || "");
  if (!stopId) return;
  decaReviewStopId.value = stopId;
  decaReviewIsModify.value = Boolean(stopItem?.has_deca);
  showDecaReviewModal.value = true;
}

function generateDecaForStop(stopItem) {
  openDecaReviewForStop(stopItem);
}

function closeDecaReviewModal() {
  showDecaReviewModal.value = false;
  decaReviewStopId.value = "";
}

function onDecaReviewConfirm() {
  const message = decaReviewIsModify.value
    ? "DeCA modificado correctamente"
    : "DeCA generado correctamente";
  closeDecaReviewModal();
  reloadPage();
  snackbar.value = {
    title: "OK",
    message,
    type: "info",
    color: "white",
  };
}

function printDecaForStop(stopItem) {
  const stopId = stopItem?._id;
  if (!stopId) return;
  decaStores
    .printDeca([stopId])
    .then((bytes) => {
      fileUtils.openBytesFile(bytes, "application/pdf");
    })
    .catch((error) => {
      snackbar.value = {
        title: "Error",
        message: errorUtils.getErrorMessageHtml(
          error,
          "No se pudo imprimir el DeCA."
        ),
        type: "danger",
        color: "white",
      };
    });
}

function onRowAction(stopItem, action) {
  const actionEvent = action?.event;
  if (!actionEvent || !stopItem) return;

  if (actionEvent === "openPlan") {
    openPlanForStop(stopItem);
    return;
  }

  if (actionEvent === "generateDeca") {
    generateDecaForStop(stopItem);
    return;
  }

  if (actionEvent === "printDeca") {
    printDecaForStop(stopItem);
    return;
  }

  if (actionEvent === "normalizeAddress") {
    normalizeAddressForStop(stopItem);
    return;
  }

  if (actionEvent === "changeDate") {
    changeDateForStop(stopItem);
    return;
  }

  if (actionEvent === "modifyManual") {
    modifyManualForStop(stopItem);
    return;
  }

  if (actionEvent === "createDeliveryFromPickup") {
    createDeliveryFromPickupForStop(stopItem);
    return;
  }

  if (actionEvent === "retryDelivery") {
    retryDeliveryForStop(stopItem);
    return;
  }

  if (actionEvent === "deleteStop") {
    deleteStopForStop(stopItem);
  }
}


onMounted(() => {
  tableStore.clear();
  const savedFilters = userStores.filtersPage.stops?.filters || [];
  tableStore.changeFilters(savedFilters);
  reloadPage();
});
watch(
  () => stopsStore.stopDeleted,
  (value) => {
    if (!value) return;
    snackbar.value = {
      title: "OK",
      message: "Parada excluida",
      type: "info",
      color: "white",
    };
    reloadPage();
  }
);

watch(
  () => stopsStore.error,
  (value) => {
    if (!value) return;
    if (showStopDetailModal.value) return;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
);
</script>

<template>
  <div class="card stops-table-view">
    <div class="card-body stops-table-body">
      <argon-table
        v-if="stopsStore.stops"
        :columns="columns"
        :items="tableItems"
        :field-filters="fieldFilters"
        :default-filters="defaultFilters"
        :import-options="importOptions"
        :show-config-option="true"
        label-button="Nuevo"
        @filter-changed="reloadPage"
        @on-reload="reloadPage"
        @sort-changed="onSortChanged"
        @page-changed="reloadPage"
        @on-click="openStopDetailModal"
        @export-data="exportStops"
        @new-item="openCreateManualStopModal"
        @download-template="downloadExcelTemplate"
      >
        <template #buttons>
          <div class="dropdown d-inline" data-dropdown-static style="--dropdown-gap: 4px">
            <argon-button
              id="navbarDropdownPrinter"
              color="primary"
              variant="outline"
              class="dropdown-toggle me-2 btn-xs"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              aria-expanded="false"
              ><i class="fa fa-exchange me-sm-2"></i>Acciones</argon-button
            >
            <ul
              class="dropdown-menu z-index-1030 dropdown-menu-lg-start px-2 py-3"
              aria-labelledby="navbarDropdownPrinter"
              style
            >
              <li
                v-tooltip="openPlanTooltip"
                :class="{ disabled: !canOpenPlan }"
              >
                <a
                  class="dropdown-item border-radius-md"
                  :aria-disabled="!canOpenPlan"
                  @click.prevent="openPlan"
                  >Ir a plan</a
                >
              </li>              
              <li>
                <hr class="dropdown-divider" />
              </li>
              <li
                v-tooltip="normalizeAddressTooltip"
                :class="{ disabled: !canNormalizeAddress }"
              >
                <a
                  class="dropdown-item border-radius-md"
                  :aria-disabled="!canNormalizeAddress"
                  @click.prevent="canNormalizeAddress && normalizeAddress()"
                  >Normalizar Dirección</a
                >
              </li>
               <li>
                <a class="dropdown-item border-radius-md" @click="changeDate"
                  >Modificar Fecha</a
                >
              </li>
              <li>
                <hr class="dropdown-divider" />
              </li>
              <li>
                <a class="dropdown-item border-radius-md" @click="retryDelivery"
                  >Nuevo Intento de Reparto</a
                >
              </li>
              <li>
                <hr class="dropdown-divider" />
              </li>
              <li>
                <a class="dropdown-item border-radius-md text-danger" @click.prevent="deleteStop"
                  >Excluir</a
                >
              </li>
            </ul>
          </div>
        </template>
        <template #default="{ item }">
          <argon-td
            :position="getPosition('date')"
            :text-primary="moment(item.date).format('DD/MM/YYYY')"
          />
          <argon-td
            :text-primary="item.number"
            :position="getPosition('number')"
          >
            <template v-if="item.number" #append>
              <copy-value :value-to-copy="String(item.number)" size="xs" show-on-hover />
            </template>
          </argon-td>
          <argon-td :position="getPosition('type')">
            <user-type-badge
              style="min-width: 100px"
              class="badge badge-md"
              :item="formatStopTypeLabel(item)"
            />
          </argon-td>
          <argon-td
            :text-primary="item.shipper_info?.name || ''"
            :text-secondary="item.contact.name"
            :position="getPosition('shipper_info.name')"
            max-width="150px"
          />
          <argon-td
            :text-primary="formatTimeWindow(item.time_window_1)"
            :text-secondary="formatTimeWindow(item.time_window_2)" 
            :position="getPosition('time_window_1')"
          />
          <argon-td
            :text-primary="getInputAddress(item)"
            :position="getPosition('address_normalized.address')"
            max-width="150px"
          />
          <argon-td
            :text-primary="item.assignment_info?.plan_name"
            :position="getPosition('assignment_info?.plan_name')"
          >
            <template v-if="item.assignment_info?.plan_name" #append>
              <copy-value :value-to-copy="item.assignment_info.plan_name" size="xs" show-on-hover />
            </template>
          </argon-td>
          <argon-td
            class="stop-alert-cell"
            :position="getPosition('alerts')"
          >
            <i
              v-if="getWarningItems(item).length > 0"
              v-tooltip="formatAlertsTooltipHtml(getWarningItems(item))"
              class="fa-solid fa-circle-exclamation stop-icon-alert"
              aria-label="Alertas"
            />
          </argon-td>
          <argon-td
            class="stop-alert-cell"
            :position="getPosition('delivery_info.summary')"
          >
            <i
              v-if="hasDeliveryInfoSummary(item)"
              v-tooltip="formatDeliveryInfoSummary(item)"
              class="fa-solid fa-circle-info grid-icon-primary grid-icon-info"
              aria-label="Info reparto"
            />
          </argon-td>
          <argon-td :position="getPosition('has_deca')">
            <i
              v-if="item.has_deca === true"
              v-tooltip="'DeCA generado'"
              class="fa fa-check grid-icon-primary text-xs"
              aria-label="DeCA generado"
            ></i>
          </argon-td>
          <argon-td :position="getPosition('status')">
            <argon-status
              entity="stop"
              :status="item.status"
              :tooltip-external-text="getStatusTooltip(item)"
              width="100px"
            />
          </argon-td>
          <argon-td :position="getPosition('actions')">
            <div class="table-row-actions">
              <actions-dropdown
                :menu-id="getRowActionsMenuId(item)"
                :actions="getRowActions(item)"
                aria-label="Acciones de parada"
                trigger-class="stop-action-button"
                menu-class="stop-action-menu-dropdown"
                @action="(action) => onRowAction(item, action)"
              />
            </div>
          </argon-td>
        </template>
      </argon-table>
    </div>
  </div>

  <stop-detail-modal
    v-if="showStopDetailModal && selectedStopId"
    modal-id="stops-table-detail-modal"
    :stop-id="selectedStopId"
    @close="closeStopDetailModal"
  />

  <stop-date-modal
    v-if="showStopDateModal && selectedStopIds.length > 0"
    modal-id="stops-table-date-modal"
    :stop-id="selectedStopId"
    :stop-ids="selectedStopIds"
    @close="closeStopDateModal"
    @success="onStopActionSuccess"
  />

  <stop-retry-delivery-modal
    v-if="showStopRetryDeliveryModal && selectedStopIds.length > 0"
    modal-id="stops-table-retry-delivery-modal"
    :stop-ids="selectedStopIds"
    @close="closeStopRetryDeliveryModal"
    @success="onStopActionSuccess"
  />

  <stop-time-window-modal
    v-if="showStopTimeWindowModal && selectedStopIds.length > 0"
    modal-id="stops-table-time-window-modal"
    :stop-id="selectedStopId"
    :stop-ids="selectedStopIds"
    @close="closeStopTimeWindowModal"
    @success="onStopActionSuccess"
  />

  <stop-normalize-address-modal
    v-if="showStopNormalizeAddressModal && selectedStopId"
    modal-id="stops-table-normalize-address-modal"
    :stop-id="selectedStopId"
    @close="closeStopNormalizeAddressModal"
    @success="onStopActionSuccess"
  />

  <create-manual-stop-modal
    v-if="showCreateManualStopModal"
    :stop-id="selectedManualStopId"
    :duplicate-from-stop-id="duplicateSourceStopId"
    @close="closeCreateManualStopModal"
    @success="onCreateManualStopSuccess"
  />

  <deca-review-modal
    v-if="showDecaReviewModal && decaReviewStopId"
    modal-id="stops-table-deca-review-modal"
    :stop-id="decaReviewStopId"
    @close="closeDecaReviewModal"
    @confirm="onDecaReviewConfirm"
  />

  <div class="position-fixed top-0 end-0 z-index-2 p-3">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped lang="scss">
@use "../../plans/components/planner-header-shared";

.stop-alert-cell {
  width: 1%;
  white-space: nowrap;
}

.stop-icon-alert {
  color: #c85c5c;
  font-size: 0.82rem;
}
:deep(.stops-table-body thead th.text-center.w-1:last-child),
:deep(.stops-table-body tbody td.text-center:last-child) {
  max-width: 48px;
  min-width: 48px;
  width: 48px;
}

</style>
